
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

import ProductBackground from '../../assets/images/ProductBackground.jpg';
import ProductECO from '../../assets/images/ProductECO.png';
import ProductAutomotive from '../../assets/images/ProductAutomotive.png';
import ProductMedical from '../../assets/images/ProductMedical.png';
import ProductWater from '../../assets/images/ProductWater.png';
import ProductTech from '../../assets/images/ProductTech.jpg';

import CertSGS from '../../assets/images/CertSGS.jpg';
import CertRoHS from '../../assets/images/CertRoHS.jpg';
import CertIAF from '../../assets/images/CertIAF.jpg';
import CertCNAS from '../../assets/images/CertCNAS.jpg';
import CertISO9001 from '../../assets/images/CertISO9001.jpg';
import CertOEKO from '../../assets/images/CertOEKO.jpg';
import CertSTAND from '../../assets/images/CertSTAND.jpg';

const ProductCharacteristicsPage = () => {
  const characteristics = [
    {
      icon: 'ri-shield-line',
      title: 'High Strength & Durability',
      description: 'Excellent tensile and tear resistance for long-lasting performance in demanding applications.',
      color: 'bg-blue-100 text-blue-600',
      benefits: ['Superior tear resistance', 'Long-lasting performance', 'Reduced replacement costs', 'Reliable under stress']
    },
    {
      icon: 'ri-feather-line',
      title: 'Lightweight Structure',
      description: 'Easier handling, reduced transportation costs, and increased design flexibility.',
      color: 'bg-green-100 text-green-600',
      benefits: ['Lower shipping costs', 'Easy installation', 'Flexible design options', 'Reduced material weight']
    },
    {
      icon: 'ri-wind-line',
      title: 'Breathability & Air Permeability',
      description: 'Promotes ventilation and comfort for furniture, automotive, and medical applications.',
      color: 'bg-cyan-100 text-cyan-600',
      benefits: ['Enhanced comfort', 'Moisture management', 'Temperature regulation', 'Improved air circulation']
    },
    {
      icon: 'ri-drop-line',
      title: 'Water & Moisture Resistance',
      description: 'Optional hydrophobic finishes provide protection against moisture and liquids.',
      color: 'bg-blue-100 text-blue-600',
      benefits: ['Liquid protection', 'Mold prevention', 'Extended lifespan', 'Weather resistance']
    },
    {
      icon: 'ri-temp-hot-line',
      title: 'Thermal & Acoustic Insulation',
      description: 'Enhances temperature and sound control in automotive and building applications.',
      color: 'bg-orange-100 text-orange-600',
      benefits: ['Energy efficiency', 'Noise reduction', 'Temperature control', 'Comfort enhancement']
    },
    {
      icon: 'ri-fire-line',
      title: 'Chemical & Flame Resistance',
      description: 'Custom-engineered to withstand specific industrial conditions and safety requirements.',
      color: 'bg-red-100 text-red-600',
      benefits: ['Safety compliance', 'Chemical stability', 'Fire retardant options', 'Industrial durability']
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Softness & Comfort',
      description: 'Smooth texture suitable for products that contact skin directly in medical and home applications.',
      color: 'bg-pink-100 text-pink-600',
      benefits: ['Skin-friendly', 'Comfortable touch', 'Hypoallergenic options', 'Gentle texture']
    },
    {
      icon: 'ri-recycle-line',
      title: 'Eco-Friendly & Recyclable',
      description: 'Produced using sustainable, non-toxic, and recyclable materials for environmental responsibility.',
      color: 'bg-green-100 text-green-600',
      benefits: ['Sustainable production', 'Recyclable materials', 'Reduced carbon footprint', 'Environmental compliance']
    },
    {
      icon: 'ri-tools-line',
      title: 'Stable & Easy to Process',
      description: 'Maintains dimensional stability and does not fray or deform during fabrication.',
      color: 'bg-purple-100 text-purple-600',
      benefits: ['Dimensional stability', 'Easy cutting', 'No fraying', 'Consistent quality']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(14, 116, 144, 0.9), rgba(14, 116, 144, 0.9)), url(${ProductBackground})`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Engineered for Excellence — Discover the Core Features of Taishun Non-Wovens
          </h1>
          <p className="text-xl md:text-2xl text-teal-100 max-w-4xl mx-auto leading-relaxed">
            Our <strong>non-woven materials are designed with precision</strong> and advanced technology to deliver 
            consistent quality and performance. Each fabric is carefully tested to meet global standards 
            across diverse applications.
          </p>
        </div>
      </section>

      {/* Core Characteristics */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Core Characteristics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every Taishun non-woven material is engineered with <strong>multiple performance features</strong> 
              to ensure optimal results in your specific application.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {characteristics.map((char, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <div className={`w-16 h-16 ${char.color} rounded-lg flex items-center justify-center mb-4`}>
                  <i className={`${char.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{char.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{char.description}</p>
                
                <div className="space-y-2">
                  {char.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Real Product Gallery
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See our <strong>high-quality non-woven materials</strong> in action across various applications and industries.
            </p>
          </div>
          
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-200">
                <img
                  src={ProductECO}
                  alt="Eco-Friendly Fabric - Recyclable Material"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">High-Performance Solutions</h3>
                  <p className="text-gray-600">Hight Strength & Durability</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-200">
                <img
                  src={ProductAutomotive}
                  alt="Automotive Application Materials"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Automotive Applications</h3>
                  <p className="text-gray-600">Lightweight &amp; Breathable</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-200">
                <img
                  src={ProductMedical}
                  alt="Medical Grade Materials"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Medical Grade Quality</h3>
                  <p className="text-gray-600">Soft &amp; Skin-Friendly</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-200">
                <img
                  src={ProductWater}
                  alt="Water Resistance Testing"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Water Resistance</h3>
                  <p className="text-gray-600">Moisture Protection Technology</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Technical Excellence
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Our <strong>advanced manufacturing processes</strong> ensure every roll of non-woven fabric 
                meets stringent quality standards. From fiber selection to final testing, 
                we maintain consistency and reliability.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-microscope-line text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Quality Testing</h4>
                    <p className="text-gray-600">Comprehensive testing for strength, durability, and performance</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Global Standards</h4>
                    <p className="text-gray-600">ISO certified manufacturing and quality management systems</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-2-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Custom Engineering</h4>
                    <p className="text-gray-600">Tailored specifications to meet unique application requirements</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
                src={ProductTech}
                alt="Technical manufacturing specifications"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Performance Metrics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our materials consistently deliver <strong>superior performance</strong> across key metrics 
              that matter most to your applications.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                metric: 'Tensile Strength',
                value: '≥50N',
                description: 'Superior resistance to tearing and stress',
                icon: 'ri-hammer-line'
              },
              {
                metric: 'Weight Range',
                value: '200-1200 GSM',
                description: 'Flexible weight options for all applications',
                icon: 'ri-scales-line'
              },
              {
                metric: 'Width Options',
                value: 'Up to 2.5m',
                description: 'Wide format capabilities for large projects',
                icon: 'ri-ruler-line'
              },
              {
                metric: 'Temperature Range',
                value: '-150°C to +500°C',
                description: 'Stable performance in extreme conditions',
                icon: 'ri-temp-cold-line'
              }
            ].map((metric, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${metric.icon} text-2xl`}></i>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{metric.metric}</div>
                <p className="text-gray-600 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Continuous Innovation
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            <strong>Taishun Global continues to innovate</strong> with new materials and processes, ensuring every roll 
            of non-woven fabric delivers unmatched reliability and sustainability. Our R&amp;D team works 
            continuously to develop next-generation solutions.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="p-6">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Research &amp; Development</h3>
              <p className="text-gray-600">Continuous innovation in material science and manufacturing processes</p>
            </div>
            
            <div className="p-6">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Sustainability Focus</h3>
              <p className="text-gray-600">Developing eco-friendly alternatives and reducing environmental impact</p>
            </div>
            
            <div className="p-6">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer Collaboration</h3>
              <p className="text-gray-600">Working closely with clients to develop custom solutions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Certifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Certified Quality Standards
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our products are backed by <strong>internationally recognized certifications</strong>, 
              ensuring compliance with global quality, safety, and environmental standards.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6 mb-12">
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertSGS}
                alt="SGS Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">SGS</h4>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertRoHS}
                alt="RoHS Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">RoHS</h4>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertIAF}
                alt="IAF Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">IAF</h4>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertCNAS}
                alt="CNAS Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">CNAS</h4>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertISO9001}
                alt="ISO 9001 Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">ISO 9001</h4>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertOEKO}
                alt="OEKO-TEX Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">OEKO-TEX</h4>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-shadow duration-200 flex flex-col items-center">
              <img
                src={CertSTAND}
                alt="STANDARD 100 Certification"
                className="w-20 h-20 object-contain mb-3"
              />
              <h4 className="text-sm font-semibold text-gray-900 text-center">STANDARD 100</h4>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Quality Assurance</h4>
                <p className="text-gray-600 text-sm">SGS, ISO 9001, and CNAS certifications ensure consistent quality</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-leaf-line text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Environmental Safety</h4>
                <p className="text-gray-600 text-sm">RoHS and OEKO-TEX compliance for eco-friendly materials</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">International Standards</h4>
                <p className="text-gray-600 text-sm">IAF and STANDARD 100 recognition for global compliance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience the Taishun Difference
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            Discover how our <strong>engineered non-woven materials</strong> can enhance your products 
            and manufacturing processes. Get detailed specifications and samples today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Download Product Catalog
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-3 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Request Sample
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductCharacteristicsPage;
